import { useEffect, useMemo } from 'react';

import * as yup from 'yup';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import { ExamsValidDateMsg, FormFooter } from 'components';
import { PaymentType } from 'core';
import { useGetOrganisationCountryPaymentSettingsQuery, useGetPaymentDetailsQuery } from 'store/payment/paymentAPI';
import { useLazyGetPaymentTncQuery } from 'store/terms-and-conditions/termsAPI';

import { useFormFields } from '@britishcouncil/react-forms';
import { Button } from '@britishcouncil/react-solas';
import { ApiErrorPage, LoadingCard, MainContainer, PageContainer, Quote } from '@britishcouncil/react-solas-ors3';

import { PaymentNotAvailableMsg } from './components/PaymentNotAvailableMsg';
import { RegistrationPaid } from './components/RegistrationPaid';
import { SelectPaymentFields } from './components/SelectPaymentFields';
import { SessionStageChangedMsg } from './components/SessionStageChangedMsg';
import { useSelectPaymentSubmit } from './useSelectPaymentSubmit';
import { getTaxInfo } from 'pages/helper/taxInfo';

export const SelectPayment = () => {
  const { referenceNumber } = useParams<{ referenceNumber: string }>();
  const {
    data: paymentDetails,
    isError: paymentDetailsError,
    isFetching: paymentDetailsLoading,
  } = useGetPaymentDetailsQuery(referenceNumber!);
  const { data: paymentSettings, isFetching: settingsLoading } = useGetOrganisationCountryPaymentSettingsQuery(
    referenceNumber!,
  );
  const [getPaymentTnc, { data: offlinePaymentTerms, isFetching: termsLoading }] = useLazyGetPaymentTncQuery();
  const { submit, isSubmitting, serverErrors } = useSelectPaymentSubmit();

  const paymentAvailable = useMemo(
    () => paymentSettings?.allowOfflinePayment || paymentSettings?.allowOnlinePayment,
    [paymentSettings],
  );
  const isLoading = useMemo(
    () => paymentDetailsLoading || settingsLoading || termsLoading,
    [paymentDetailsLoading, settingsLoading, termsLoading],
  );

  const taxInfo = useMemo(() => {
    if (!paymentDetails) return '';
    return getTaxInfo(paymentDetails.taxRate, paymentDetails.fullFee);
  }, [paymentDetails]);

  const formik = useFormik<{ paymentType: number }>({
    validationSchema: yup.object().shape({
      paymentType: yup.number().min(0, 'Please select payment type.'),
    }),
    initialValues: { paymentType: -1 },
    onSubmit: ({ paymentType }) => submit({ paymentType, referenceNumber: referenceNumber! }),
  });
  const fields = useFormFields(formik);

  useEffect(() => {
    if (!!paymentDetails && paymentDetails.fullFee === 0) {
      submit({ paymentType: PaymentType.OfflinePayment, referenceNumber: referenceNumber! });
    }
  }, [paymentDetails, referenceNumber, submit]);

  useEffect(() => {
    if (paymentSettings && paymentSettings.countryId) {
      getPaymentTnc(paymentSettings.countryId);
    }
  }, [getPaymentTnc, paymentSettings]);

  if (paymentDetails && paymentDetails.isPaid) return <RegistrationPaid paymentDetails={paymentDetails} />;

  const onlinePaymentFee =
    paymentDetails?.onlinePaymentFee && paymentDetails.onlinePaymentFee > 0
      ? paymentDetails.onlinePaymentFee
      : paymentDetails?.fullFee;

  return (
    <>
      <MainContainer>
        <h1>Select payment method</h1>
      </MainContainer>

      {isLoading && <LoadingCard>One second, please</LoadingCard>}
      {paymentDetailsError && <ApiErrorPage errorMessage="Something went wrong. Please try again later" />}

      {!isLoading && !paymentDetailsError && (
        <>
          <MainContainer>
            {paymentDetails?.sessionStageChanged && <SessionStageChangedMsg />}

            {/* TODO[GP - future fix]: we should fetch this info from api instead get it from basket store... */}
            <ExamsValidDateMsg />

            {!paymentAvailable && <PaymentNotAvailableMsg />}
          </MainContainer>

          {paymentAvailable && (
            <PageContainer className="page-container">
              <h2> Choose a payment method below to complete your booking.</h2>
              <Quote title="Note" intent="info">
                Your exam centre may also add an administration charge to this fee.
              </Quote>
              <form onSubmit={formik.handleSubmit}>
                <SelectPaymentFields
                  fields={fields}
                  paymentSettings={paymentSettings}
                  offlinePaymentTerms={offlinePaymentTerms?.text}
                  fee={`${paymentDetails?.fullFee.toFixed(2)} ${paymentDetails?.currency}`}
                  onlinePaymentFee={`${onlinePaymentFee?.toFixed(2)} ${paymentDetails?.currency}`}
                  taxInfo={taxInfo}
                />
                <FormFooter
                  mainErrors={!isSubmitting ? serverErrors : undefined}
                  toastrError={formik.errors.paymentType}
                  isValid={formik.isValid}
                  submitCount={formik.submitCount}
                >
                  <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                    Continue
                  </Button>
                </FormFooter>
              </form>
            </PageContainer>
          )}
        </>
      )}
    </>
  );
};
